import React, { useEffect, useState } from "react";
import { PageHeading } from "../../components/ui/PageHeading";
import SearchInput from "../../components/ui/SearchInput";
import CommonDataTable from "../../components/ui/table/CommonTable";
import RegistrationForm from "./RegistrationForm";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { registrationDB } from "../../utils/offlineDB";

import {
  useGetRfidRegistrationsQuery,
  useAddRfidRegistrationMutation,
  useUpdateRfidRegistrationMutation,
  useDeleteRfidRegistrationMutation,
  useGetRfidDropdownQuery,
} from "../../featureSlice/rfidRegistrationSliceApi";
import { Button } from "../../components/ui/button";
import DeleteModal from "../../components/ui/DeleteModal";
import EditIcon from "../../svg/EditIcon";
import DeleteIcon from "../../svg/DeleteIcon";
import Download from "../../svg/Download";

const Registration = () => {
  const [page, setPage] = useState(1);

  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [editingRow, setEditingRow] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [search, setSearch] = useState("");

  // 🔹 GET
  const { data, isLoading } = useGetRfidRegistrationsQuery({
    page,
    limit: rowsPerPage,

    search,
  });

  const { data: dropdownData, isLoading: isDropdownLoading } =
    useGetRfidDropdownQuery();

  // 🔹 MUTATIONS
  const [addRfid] = useAddRfidRegistrationMutation();
  const [updateRfid] = useUpdateRfidRegistrationMutation();
  const [deleteRfid] = useDeleteRfidRegistrationMutation();

  const rows = data?.data || [];

  const filteredRows = rows.filter((row) => {
    if (!search) return true;

    const keyword = search.toLowerCase();

    return (
      row.rfid_uid?.toLowerCase().includes(keyword) ||
      row.employee_code?.toLowerCase().includes(keyword) ||
      row.card_token_number?.toLowerCase().includes(keyword) ||
      row.rfid_registration_for?.toLowerCase().includes(keyword)
    );
  });
  const getReadableErrorMessage = (err) => {
    const msg = err?.data?.message || "";

    // 🔹 Employee Code duplicate
    if (msg.includes("employee_code")) {
      const match = msg.match(/employee_code:\s*\"(.*?)\"/);
      const code = match?.[1];

      return code
        ? `Employee code ${code} already has an RFID assigned.`
        : "This employee already has an RFID assigned.";
    }

    // 🔹 RFID UID duplicate
    if (msg.includes("rfid_uid")) {
      return "This RFID UID is already registered.";
    }

    // 🔹 Card token duplicate
    if (msg.includes("card_token")) {
      return "This card token is already in use.";
    }

    // 🔹 Generic duplicate
    if (msg.toLowerCase().includes("duplicate")) {
      return "Duplicate record found. Please check existing entries.";
    }

    return "Something went wrong. Please try again.";
  };

  // 🔹 Submit (POST / UPDATE)
  const handleSubmit = async (formData) => {
    try {
      if (editingRow) {
        await updateRfid({
          id: editingRow.id,
          ...formData,
        }).unwrap();
        toast.success("Registration updated successfully ✅");
      } else {
        await addRfid(formData).unwrap();
        toast.success("Registration added successfully 🎉");
      }
      setEditingRow(null);
      setIsFormOpen(false);
    } catch (err) {
      const message = getReadableErrorMessage(err);
      toast.error(message);
      console.error("Submit failed", err);
    }
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setIsDeleteOpen(true);
  };

  const handleDeleteAction = async () => {
    try {
      await deleteRfid(deleteId).unwrap();
      toast.success("Registration deleted");

      setIsDeleteOpen(false);
      setDeleteId(null);
    } catch (err) {
      toast.error("Delete failed");

      console.error("Delete failed", err);
    }
  };

  useEffect(() => {
    if (!data?.data) return;

    const saveLocal = async () => {
      for (const item of data.data) {
        await registrationDB.setItem(item.rfid_uid, item);
      }
    };

    saveLocal();
  }, [data]);

  // 🔹 Table columns
  const columns = [
    { name: "RFID UID", selector: (row) => row?.rfid_uid },
    {
      name: "Type",
      selector: (row) => row?.rfid_registration_for,
    },
    {
      name: "Employee Code",
      selector: (row) => row?.employee_code || "-",
    },
    {
      name: "Card Token",
      selector: (row) => row?.card_token_number || "-",
    },
    {
      name: "Sub Category",
      selector: (row) => row?.rfid_registration_for_subcategory || "-",
    },
    {
      name: " Name",
      selector: (row) => row?.rfid_owner_name || "-",
    },
    {
      name: "Action",
      cell: (row) => (
        <div className="flex gap-2">
          <button
            className="text-blue-600"
            onClick={() => {
              setEditingRow(row);
              setIsFormOpen(true);
            }}
          >
            <EditIcon />
          </button>
          <button
            className="text-red-600"
            onClick={() => handleDeleteClick(row.id)}
          >
            <DeleteIcon />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="p-5 space-y-5">
      <ToastContainer />

      {/* ===== PAGE HEADING ===== */}
      <PageHeading title="Registration" />

      {/* ===== SEARCH ===== */}
      <div className="flex justify-between">
      <h4>Total Registration: <span className="text-darkGray text-lg">{data?.meta_data?.total_records || 0}</span></h4>
      <div className="flex gap-3 justify-end">

        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search RFID / Employee / Card"
        />

        <Button
          className="bg-primary text-white text-2xl "
          onClick={() => {
            setEditingRow(null);
            setIsFormOpen(true);
          }}
        >
          <span className=" pr-1">+</span> Add Registration
        </Button>

        <Button
          className="bg-white text-primary font-bold logout-btn"
          disabled={isDropdownLoading}
          onClick={() => {
            if (dropdownData?.data?.length > 0) {
              const headers = [
                "ID",
                "RFID UID",
                "Registration For",
                "Sub Category",
                "Employee Code",
                "Owner Name",
                "Card Token Number",
                "Status",
              ];

              // CSV Rows
              const rows = dropdownData.data.map((item) => [
                item.id || "-",
                item.rfid_uid || "-",
                item.rfid_registration_for || "-",
                item.rfid_registration_for_subcategory || "-",
                item.employee_code || "-",
                item.rfid_owner_name || "-",
                item.card_token_number || "-",
                item.rfid_registration_status ? "Active" : "Inactive",
              ]);

              // Combine headers + rows
              const csvContent = [headers, ...rows]
                .map((row) => row.join(","))
                .join("\n");

              // Download
              const blob = new Blob([csvContent], {
                type: "text/csv;charset=utf-8;",
              });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = "registrations.csv";
              a.click();
              URL.revokeObjectURL(url);
            } else {
              toast.error("No data available to download");
            }
          }}
        >
          <Download/>
        </Button>
      </div>
      </div>

      {/* ===== FORM MODAL ===== */}
      {isFormOpen && (
        <RegistrationForm
          initialData={editingRow}
          close={() => setIsFormOpen(false)}
          onSubmit={handleSubmit}
        />
      )}

      {/* ===== TABLE ===== */}
      <CommonDataTable
        columns={columns}
        // data={rows}
        data={filteredRows}
        progressPending={isLoading}
        pagination
        paginationServer
        paginationTotalRows={data?.meta_data?.total_records || 0}
        onChangePage={(page) => setPage(page)}
        onChangeRowsPerPage={(newLimit) => {
          setRowsPerPage(newLimit);
          setPage(1);
        }}
      />

      <DeleteModal
        isOpen={isDeleteOpen}
        closeModal={() => setIsDeleteOpen(false)}
        handleDeleteAction={handleDeleteAction}
        itemId={deleteId}
      />
    </div>
  );
};

export default Registration;
